"use client"

import React, { useState } from "react"
import { z } from "zod"
import { toast } from "sonner"
import Input from "@/components/atoms/Input/input"
import { Button } from "@/components/atoms/Button/button"
import { submitNewsletterSubscription } from "@/app/actions/user/user.action"

// Define Zod schema for email validation
const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
})

type FormData = z.infer<typeof emailSchema>

const EmailSubscriptionForm: React.FC<{ placeholder: string, button: string }> = ({ placeholder, button }) => {
  const [formData, setFormData] = useState<FormData>({ email: "" })
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Partial<FormData>>({})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setErrors({})

    try {
      const validatedData = emailSchema.parse(formData)

      await submitNewsletterSubscription(validatedData.email)

      setFormData({ email: "" })
      toast.success("Thank you for subscribing!")
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors = error.formErrors.fieldErrors

        setErrors({
          email: fieldErrors.email?.[0] || "",
        })

        toast.error("Please check the highlighted fields and try again.")
      } else {
        toast.error("We couldn’t complete your subscription right now. Please try again in a moment.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    if (errors.email) setErrors({ ...errors, email: "" })
  }

  return (
    <form onSubmit={handleSubmit} className="mt-6 w-full">
      <div className="flex flex-col items-center justify-center space-y-4 md:flex-row md:justify-start md:space-x-4 md:space-y-0">
        <div className="w-full md:w-2/3">
          <Input
            type="email"
            name="email"
            placeholder={placeholder}
            className={`w-full rounded-md border p-4 transition duration-200 focus:outline-none focus:ring-2 ${errors.email
                ? "border-red-500 focus:ring-red-300"
                : "border-gray-300 focus:ring-blue-400"
              }`}
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        <Button
          type="submit"
          className={`delay-50 h-full min-h-12 min-w-32 items-center justify-center rounded px-6 py-2.5 text-center text-lg md:w-auto w-full text-white transition-colors ${isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-700"
            }`}
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center">
              <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </span>
          ) : (
            button
          )}
        </Button>
      </div>
    </form>
  )
}

export default EmailSubscriptionForm